
import React from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TrendingDown, TrendingUp, LucideIcon, Loader2 } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import type { DashboardMetric } from "@/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

interface MetricCardProps {
  metric: DashboardMetric & {
    icon?: LucideIcon;
    timeFrames?: readonly ['week', 'month', 'year'];
    currentTimeFrame?: string;
    onTimeFrameChange?: (timeFrame: string) => void;
    onClick?: () => void;
    isLoading?: boolean;
  };
}

function MetricCardComponent({ metric }: MetricCardProps) {
  const Icon = metric.icon;

  const getTrendIcon = () => {
    if (!metric.trend) return null;
    return metric.trend === 'up' ? (
      <TrendingUp className="w-4 h-4 text-green-400" />
    ) : (
      <TrendingDown className="w-4 h-4 text-red-400" />
    );
  };

  const getChangeColor = () => {
    if (!metric.change) return "text-gray-300";
    
    if (metric.label === "Removed Clients") {
      return metric.change > 0 ? "text-red-400" : "text-green-400";
    }
    
    return metric.trend === 'up' ? "text-green-400" : "text-red-400";
  };

  const getChartColor = () => {
    return "#D6BCFA"; // Light purple color for all charts
  };

  // Transform the data to include sequential index for proper x-axis positioning
  const chartData = (metric.chartData || []).map((value, index) => ({ 
    value,
    index 
  }));
  
  const chartColor = getChartColor();
  
  const getYAxisDomain = () => {
    const values = metric.chartData || [];
    if (values.length === 0) return ['auto', 'auto'];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    // Handle flat lines (all values are the same)
    if (min === max) {
      const value = min;
      return [Math.max(0, value * 0.8), value * 1.2]; // Create some spacing around the line
    }
    
    const padding = (max - min) * 0.2;
    return [Math.max(0, min - padding), max + padding];
  };

  return (
    <Card 
      className={`hover:shadow-lg transition-shadow duration-200 backdrop-blur-lg bg-white/10 border-white/20 ${metric.onClick ? 'cursor-pointer' : ''}`}
      onClick={metric.onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-white/70">{metric.label}</h3>
          <div className="flex items-center gap-2">
            {metric.timeFrames && (
              <Select
                value={metric.currentTimeFrame}
                onValueChange={metric.onTimeFrameChange}
              >
                <SelectTrigger className="w-24 h-7 text-xs bg-white/5 border-white/10" onClick={(e) => e.stopPropagation()}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {metric.timeFrames.map((tf) => (
                    <SelectItem key={tf} value={tf}>
                      {tf.charAt(0).toUpperCase() + tf.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {Icon && <Icon className="w-4 h-4 text-white/70" />}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {metric.isLoading ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-8 w-12 bg-white/10" />
              <Skeleton className="h-6 w-16 bg-white/10" />
            </div>
            <Skeleton className="h-16 w-full mt-6 bg-white/5" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="text-2xl font-bold text-white">{metric.value}</div>
              {(metric.change !== undefined || metric.trend) && (
                <div className="flex items-center gap-1">
                  {metric.change !== undefined && (
                    <span className={`text-sm font-medium ${getChangeColor()}`}>
                      {metric.change > 0 ? "+" : ""}
                      {metric.change}%
                    </span>
                  )}
                  {getTrendIcon()}
                </div>
              )}
            </div>
            {metric.chartData && metric.chartData.length > 0 && (
              <div className="h-16 mt-6">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id={`colorGradient-${metric.label.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={chartColor} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={chartColor} stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <YAxis 
                      hide
                      domain={getYAxisDomain()} 
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div className="bg-black/80 px-2 py-1 rounded text-xs text-white">
                              {payload[0].value}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={chartColor}
                      fill={`url(#colorGradient-${metric.label.replace(/\s+/g, '')})`}
                      strokeWidth={2}
                      isAnimationActive={true}
                      connectNulls={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export const MetricCard = React.memo(MetricCardComponent, (prevProps, nextProps) => {
  const prevMetric = prevProps.metric;
  const nextMetric = nextProps.metric;
  
  return (
    prevMetric.label === nextMetric.label &&
    prevMetric.value === nextMetric.value &&
    prevMetric.change === nextMetric.change &&
    prevMetric.trend === nextMetric.trend &&
    prevMetric.isLoading === nextMetric.isLoading &&
    prevMetric.currentTimeFrame === nextMetric.currentTimeFrame &&
    JSON.stringify(prevMetric.chartData) === JSON.stringify(nextMetric.chartData)
  );
});
